import { NextPageContext } from 'next';

import { DoRequest } from '@r1-frontend/do-request/core/DoRequest';
import getCityInfo, { ResponseDto as CityInfoResponseDto } from '@r1-frontend/api-domru/api-content/v1/geography/get-city-info';
import { ResponseDto as MenuResponseDto } from '@r1-frontend/api-domru/api-content/v2/menu/by-types';
import menuByVariantAliases from '@r1-frontend/api-domru/api-content/v2/menu/by-variant-aliases';

import Cookies from '@r1-frontend/shared/helpers/Cookies';
import { generateAbtTestValue } from '@r1-frontend/shared/helpers/generateAbtTestValue';
import { IRequest } from '@r1-frontend/shared/types/IRequest';

import { prepareCity } from '~/src/api/content/geography/prepare-city';
import {
    ABN_TEST_COOKIE,
    ABN_VARIANTS_COOKIE,
    CITYDOMAIN_COOKIE,
    COOKIE_MAX_AGE_LONG,
    COOKIE_MAX_AGE_SHORT,
    PROVIDER_ID_COOKIE,
} from '~/src/constants';
import { REDESIGN_MENU_UNAUTH } from '~/src/features/index/constants/abnVariants';
import { SHOWCASE } from '~/src/features/showcase/constants/abnVariants';
import { getABNVariantFrom, getABNVariants } from '~/src/helpers/abn-tests';
// todo: дописать Cookies.load, заменить тут и добавить юнит тесты, когда какие-то куки есть
import { getCookie } from '~/src/helpers/cookie';
import getRequester from '~/src/helpers/getRequester';
import { formatterMenuDataByResponse } from '~/src/store/actions/menu';
import { INewMenuItems } from '~/src/store/reducers/menu';

const LONG_COOKIES_CONFIG = { maxAge: COOKIE_MAX_AGE_LONG };
const SHORT_COOKIES_CONFIG = { maxAge: COOKIE_MAX_AGE_SHORT };
const SEC_IN_YEAR = 31536000;


class InitialCity {
    private readonly doRequest: DoRequest<IRequest>;
    private readonly cookies: Cookies;

    constructor(private readonly ctx: NextPageContext) {
        if (!ctx || !ctx.req || !ctx.res) {
            throw new Error('[baseInitialProps] InitialCity could not be initialized without Next Context');
        }

        this.doRequest = getRequester(ctx.req);
        this.cookies = new Cookies(ctx);
    }

    public async getAll(
    ): Promise<{ providerId: number, currentCity: ReturnType<typeof prepareCity>, abnVariants: string, menus: INewMenuItems }> {
        const cityInfo = await this.getCityInfo();
        const providerId = cityInfo.provider_id;

        const currentCity = this.getCurrentCity(cityInfo);
        const abnVariants = this.getAbnVariants(cityInfo);
        const menus = await this.getMenus(providerId, abnVariants);

        return {
            providerId,
            currentCity,
            abnVariants,
            menus,
        };
    }

    private async getCityInfo() {
        const cityFromHost = this.ctx.query['city'] || '';
        const domain = Array.isArray(cityFromHost)
            ? cityFromHost[0]
            : cityFromHost;

        const providerIdCookie = getCookie(PROVIDER_ID_COOKIE, this.ctx.req);
        const providerId = providerIdCookie ? Number(providerIdCookie) : 0;

        // todo: Добавить in memory cache
        const response = await this.doRequest.apiContent<CityInfoResponseDto>(
            getCityInfo({
                expand: 'promolines',
                /**
                 * Для ситуации когда у нас в одном городе несколько провайдеров в не АЗ мы всегда отображаем активного провайдера,
                 * а в АЗ должен отображаться текущий город в зависимости от куки providerId.
                 */
                ...providerId ? { providerId, onlyActive: false } : { domain },
            }),
        );
        if (!response.isSuccess) {
            throw new Error('[baseInitialProps] Unknown current city. Api-content /v1/geography/get-city-info request failed!');
        }

        return response.payload;
    }

    private getCurrentCity(cityInfo: CityInfoResponseDto) {
        const nextCurrentCity = prepareCity(cityInfo);

        this.cookies.saveAll([
            { name: CITYDOMAIN_COOKIE, value: nextCurrentCity.domain, config: LONG_COOKIES_CONFIG },
            { name: PROVIDER_ID_COOKIE, value: cityInfo.provider_id, config: SHORT_COOKIES_CONFIG },
        ]);

        return nextCurrentCity;
    }

    private getAbnVariants(cityInfo: CityInfoResponseDto) {
        let abnTestValue = getCookie(ABN_TEST_COOKIE, this.ctx.req);

        if (!abnTestValue) {
            abnTestValue = generateAbtTestValue();
        }
        const nextVariants = getABNVariants(cityInfo.abnTests, abnTestValue);

        this.cookies.saveAll([
            { name: ABN_TEST_COOKIE, value: abnTestValue, config: { maxAge: SEC_IN_YEAR } },
            { name: ABN_VARIANTS_COOKIE, value: nextVariants, config: SHORT_COOKIES_CONFIG },
        ]);

        return nextVariants;
    }

    private async getMenus(providerId: number, abnVariants: string) {
        const abnTests = [
            SHOWCASE,
            REDESIGN_MENU_UNAUTH,
            /**
             * Add your abn-tests here ...
             */
        ];

        const abnVariantsAliases = abnTests.map(test => getABNVariantFrom(test.testName, abnVariants)).join(',');

        // todo: Добавить in memory cache
        const resp = await this.doRequest.apiContent<MenuResponseDto>(
            menuByVariantAliases({
                providerId,
                abnVariantsAliases,
            }),
        );

        if (!resp.isSuccess) {
            throw new Error('[baseInitialProps] Cannot receive menu data. Api-content /v2/menu/by-variant-aliases request failed!');
        }

        return formatterMenuDataByResponse(resp.payload);
    }
}

export default InitialCity;
